import { useState, useEffect } from 'react';
import { 
  Search, 
  Plus, 
  Package, 
  History, 
  Edit2, 
  
  X,
  Loader2,
  
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
// Removed unused Tabs imports
import { 
  partsInventoryService, 
  Part, 
  PartCategory, 
  
  Transaction 
} from '../../utils/services/partsInventoryService';
import { cn } from '../ui/utils';

interface InventoryManagementProps {
  onClose?: () => void;
  currentUser: { id: string; name: string };
}

export default function InventoryManagement({ onClose, currentUser }: InventoryManagementProps) {
  // removed unused tab state
  const [parts, setParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showAddPart, setShowAddPart] = useState(false);
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Form State
  const [formData, setFormData] = useState<Partial<Part>>({
    category: 'other',
    unitOfMeasure: 'each',
    minQuantity: 0,
    maxQuantity: 100,
    reorderPoint: 5,
    reorderQuantity: 10,
    unitCost: 0,
    location: { warehouse: 'Main' }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadParts();
  }, []);

  const loadParts = async () => {
    setLoading(true);
    try {
      const data = await partsInventoryService.getPartsList();
      setParts(data);
    } catch (error) {
      console.error('Failed to load parts:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePart = async () => {
    if (!formData.name || !formData.partNumber) {
      toast.error('Name and Part Number are required');
      return;
    }

    setSaving(true);
    try {
      if (selectedPart) {
        await partsInventoryService.updatePart(selectedPart.id, formData, currentUser.id);
        toast.success('Part updated successfully');
      } else {
        await partsInventoryService.createPart(
          formData as any,
          currentUser.id,
          currentUser.name
        );
        toast.success('Part created successfully');
      }
      setShowAddPart(false);
      setSelectedPart(null);
      setFormData({});
      loadParts();
    } catch (error) {
      console.error('Failed to save part:', error);
      toast.error('Failed to save part');
    } finally {
      setSaving(false);
    }
  };

  const handleViewTransactions = async (part: Part) => {
    setSelectedPart(part);
    setShowTransactions(true);
    setLoadingTransactions(true);
    try {
      const history = await partsInventoryService.getTransactionHistory({ partId: part.id });
      setTransactions(history);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      toast.error('Failed to load history');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const filteredParts = parts.filter(part => {
    const matchesSearch = 
      part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.partNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || part.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const categories: PartCategory[] = [
    'pump_parts', 'engine_parts', 'electrical', 'plumbing', 
    'seals_gaskets', 'bearings', 'filters', 'valves', 
    'fittings', 'fasteners', 'lubricants', 'chemicals', 
    'safety_equipment', 'tools', 'consumables', 'other'
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <button
              type="button"
              title="Close"
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold">Inventory Management</h1>
              <p className="text-sm text-blue-100">Manage parts, stock levels, and transactions</p>
            </div>
          </div>
          <Button onClick={() => {
          setSelectedPart(null);
          setFormData({
            category: 'other',
            unitOfMeasure: 'each',
            minQuantity: 0,
            maxQuantity: 100,
            reorderPoint: 5,
            reorderQuantity: 10,
            unitCost: 0,
            location: { warehouse: 'Main' }
          });
          setShowAddPart(true);
        }} className="bg-white text-blue-700 hover:bg-white/90">
          <Plus className="h-4 w-4 mr-2" />
          Add Part
        </Button>
      </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        {/* Sidebar / Filters */}
        <div className="w-full md:w-64 bg-white border-r p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Search parts..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="pt-4 border-t">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quick Stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Parts</span>
                <span className="font-medium">{parts.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Low Stock</span>
                <span className="font-medium text-orange-600">
                  {parts.filter(p => p.quantityAvailable <= p.reorderPoint).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Out of Stock</span>
                <span className="font-medium text-red-600">
                  {parts.filter(p => p.quantityOnHand === 0).length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Parts List */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredParts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Package className="h-12 w-12 mb-2 opacity-20" />
              <p>No parts found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredParts.map(part => (
                <Card key={part.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base font-semibold">{part.name}</CardTitle>
                        <CardDescription className="font-mono text-xs mt-1">{part.partNumber}</CardDescription>
                      </div>
                      <Badge variant={part.quantityAvailable <= part.reorderPoint ? 'destructive' : 'outline'}>
                        {part.quantityAvailable} {part.unitOfMeasure}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Category:</span>
                        <span className="capitalize">{part.category.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Location:</span>
                        <span>{part.location.warehouse} {part.location.shelf ? `- ${part.location.shelf}` : ''}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Cost:</span>
                        <span>${part.unitCost.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => {
                          setSelectedPart(part);
                          setFormData(part);
                          setShowAddPart(true);
                        }}
                      >
                        <Edit2 className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleViewTransactions(part)}
                      >
                        <History className="h-3 w-3 mr-1" /> History
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Part Dialog */}
      
      <Dialog open={showAddPart} onOpenChange={setShowAddPart}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPart ? 'Edit Part' : 'Add New Part'}</DialogTitle>
            <DialogDescription>
              {selectedPart ? 'Update part details and inventory settings.' : 'Create a new part record in the inventory system.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Part Name *</label>
              <Input 
                value={formData.name || ''} 
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="e.g. 2-inch Ball Valve"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Part Number *</label>
              <Input 
                value={formData.partNumber || ''} 
                onChange={e => setFormData({...formData, partNumber: e.target.value})}
                placeholder="e.g. VLV-200-BALL"
                disabled={!!selectedPart} // Auto-generated usually, but editable on create if manual
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">Description</label>
              <Input 
                value={formData.description || ''} 
                onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Detailed description..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select 
                value={formData.category} 
                onValueChange={(val: any) => setFormData({...formData, category: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Unit of Measure</label>
              <Select 
                value={formData.unitOfMeasure} 
                onValueChange={(val: any) => setFormData({...formData, unitOfMeasure: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['each', 'box', 'case', 'gallon', 'foot', 'set'].map(uom => (
                    <SelectItem key={uom} value={uom}>{uom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Unit Cost ($)</label>
              <Input 
                type="number"
                value={formData.unitCost} 
                onChange={e => setFormData({...formData, unitCost: parseFloat(e.target.value)})}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Reorder Point</label>
              <Input 
                type="number"
                value={formData.reorderPoint} 
                onChange={e => setFormData({...formData, reorderPoint: parseFloat(e.target.value)})}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Warehouse Location</label>
              <Input 
                value={formData.location?.warehouse || ''} 
                onChange={e => setFormData({
                  ...formData, 
                  location: { ...formData.location!, warehouse: e.target.value }
                })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Shelf/Bin</label>
              <Input 
                value={formData.location?.shelf || ''} 
                onChange={e => setFormData({
                  ...formData, 
                  location: { ...formData.location!, shelf: e.target.value }
                })}
                placeholder="Optional"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPart(false)}>Cancel</Button>
            <Button onClick={handleSavePart} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Part
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transactions History Dialog */}
      <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transaction History</DialogTitle>
            <DialogDescription>
              History for {selectedPart?.name} ({selectedPart?.partNumber})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingTransactions ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : transactions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No transactions found.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map(txn => (
                      <tr key={txn.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date((txn.transactionDate as any).toDate?.() || txn.transactionDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline" className={cn(
                            txn.type === 'receipt' ? 'bg-green-50 text-green-700 border-green-200' :
                            txn.type === 'issue' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-gray-50 text-gray-700'
                          )}>
                            {txn.type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {txn.type === 'issue' || txn.type === 'sale' ? '-' : '+'}{txn.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{txn.performedByName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{txn.transactionNumber}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
